import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { useSideNavActive } from '../LayoutWrapper/SideNavLayoutContext';
import { FC, useEffect, useMemo, useState } from 'react';
import { selectedReportsFilter, setReportsFilter } from '../../redux/reducers/filterReportsSlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectedCsvProcess } from '../../redux/reducers/csvProcessSlice';
import { Site } from '../../models/Site';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { useLazyLoadQuery } from 'react-relay';
import { allClassesBasicData, allSites, classesByTeacherIdOnlyClasses } from '../../graphQLOperations/Classes';
import {
  ClassesGetAllBasicDataQuery,
  ClassesGetAllBasicDataQuery$data,
} from '../../graphQLOperations/__generated__/ClassesGetAllBasicDataQuery.graphql';
import {
  ClassesGetByTeacherIdQuery,
  ClassesGetByTeacherIdQuery$data,
} from '../../graphQLOperations/__generated__/ClassesGetByTeacherIdQuery.graphql';
import { Roles } from '../../models/User';
import { selectUser } from '../../redux/reducers/userSlice';
import { ClassesAllSitesQuery } from '../../graphQLOperations/__generated__/ClassesAllSitesQuery.graphql';
import { sortClassesList } from '../../utils/sortClassesList';
import { useHiddenClassIds } from '../../custom-hooks/useHiddenClassIds';
import { Class } from '../../models/Class';
import { Grade } from '../../models/Grade';
import ClassesRosterSelectClassDialog from '../ClassesRoster/ClassesRosterDialog/ClassesRosterSelectClassDialog';
import {
  selectedDiagnosticReportsFilter,
  setDiagnosticReportsFilter,
} from '../../redux/reducers/filterDiagnosticReport';
import { formatClassDisplayName } from '../../utils/formatClassDisplayName';
import { useLocation } from 'react-router-dom';
interface Props {
  singleClassDropdown?: boolean;
  roadmapRequired?: boolean;
}
const MonitoringCommonFilters: FC<Props> = ({ singleClassDropdown, roadmapRequired = true }) => {
  const dispatch = useAppDispatch();
  const reportsFilterRedux = useAppSelector(selectedReportsFilter);
  const diagnosticReportFilters = useAppSelector(selectedDiagnosticReportsFilter);
  const isCsvProcessing = useAppSelector(selectedCsvProcess);
  const userRedux = useAppSelector(selectUser);
  const adminRoles = [Roles.NETWORK_ADMIN, Roles.SITE_ADMIN];
  const isAdmin = adminRoles.some(role => userRedux?.roles?.some(userRole => userRole?.name === role));
  const isNetworkAdmin = userRedux?.roles?.some(role => role?.name === Roles.NETWORK_ADMIN);
  const isTeacher = userRedux?.roles?.some(role => role?.name === Roles.TEACHER);
  const sideNavActive = useSideNavActive();
  const hideClassFilter = sideNavActive && !isAdmin;
  const [visibleSelectClassDialog, setVisibleSelectClassDialog] = useState<boolean>(false);
  const [classesList, setClassesList] = useState<Class[]>([]);
  const location = useLocation();

  const sitesData = useLazyLoadQuery<ClassesAllSitesQuery>(
    allSites,
    { input: { userId: 'UserNode:' + userRedux?.id } },
    { fetchPolicy: 'network-only' },
  );

  const classesData = isAdmin
    ? useLazyLoadQuery<ClassesGetAllBasicDataQuery>(
        allClassesBasicData,
        { cursor: null, first: 5000 },
        { fetchPolicy: 'network-only' },
      )
    : useLazyLoadQuery<ClassesGetByTeacherIdQuery>(
        classesByTeacherIdOnlyClasses,
        { teacherId: 'UserNode:' + userRedux?.id },
        { fetchPolicy: 'network-only' },
      );
  const sitesTemplate: Site[] = useMemo(
    () => (sitesData?.sites?.nodes?.filter(Boolean) as Site[] | undefined) ?? [],
    [sitesData?.sites?.nodes],
  );

  const hiddenClassIds = useHiddenClassIds();

  const classesTemplates: Class[] = useMemo(() => {
    const base = isAdmin
      ? (classesData as ClassesGetAllBasicDataQuery$data)?.classes?.nodes
      : isTeacher && sitesTemplate?.length > 1
      ? (classesData as ClassesGetByTeacherIdQuery$data)?.classesByTeacherId
      : (classesData as ClassesGetByTeacherIdQuery$data)?.classesByTeacherId;
    return ((base?.filter(Boolean) as Class[] | undefined) ?? []).filter(cls => !hiddenClassIds.has(cls.id));
  }, [isAdmin, isTeacher, sitesTemplate, classesData, hiddenClassIds]);

  const selectedSite: Site | undefined =
    (reportsFilterRedux?.sites as Site | undefined) ?? (sitesTemplate.length === 1 ? sitesTemplate[0] : undefined);

  const hasSite = !!selectedSite?.id;
  const hasClasses = singleClassDropdown
    ? !!diagnosticReportFilters?.classes?.[0]
    : (reportsFilterRedux?.classes?.length ?? 0) > 0;

  const hasRoadmap = singleClassDropdown ? !!diagnosticReportFilters?.roadmap?.id : !!reportsFilterRedux?.roadmaps?.id;

  useEffect(() => {
    if (userRedux?.sites?.length === 1) {
      dispatch(setReportsFilter({ ...reportsFilterRedux, sites: userRedux.sites[0] }));
    }
  }, []);

  useEffect(() => {
    if (!hasSite) {
      setClassesList([]);
      return;
    }
    const mapClasses = (cl: Class) => {
      const newName = formatClassDisplayName(cl);
      return {
        ...cl,
        sectionName: newName,
        name: newName,
        grades: cl?.gradeLevels?.map(grade => grade?.name)?.join(', '),
      };
    };
    if (
      (isNetworkAdmin && reportsFilterRedux?.sites) ||
      (isNetworkAdmin && sitesTemplate?.length === 1) ||
      !isNetworkAdmin
    ) {
      let filtered: Class[] = classesTemplates;
      if (hasSite) {
        const siteId =
          sitesTemplate.length === 1 ? sitesTemplate[0]?.id : reportsFilterRedux.sites?.id ?? selectedSite?.id;
        if (siteId) {
          filtered = filtered.filter(cl => cl?.site?.id === siteId);
        }
      }
      const mapped = sortClassesList(filtered.map(mapClasses));
      setClassesList(prev => {
        if (prev.length === mapped.length) {
          let same = true;
          for (let i = 0; i < prev.length; i++) {
            const prevClass = prev[i] as Class & { sectionName?: string; grades?: string };
            const mappedClass = mapped[i] as Class & { sectionName?: string; grades?: string };
            if (
              prevClass.id !== mappedClass.id ||
              prevClass.name !== mappedClass.name ||
              prevClass.sectionName !== mappedClass.sectionName ||
              prevClass.grades !== mappedClass.grades
            ) {
              same = false;
              break;
            }
          }
          if (same) return prev;
        }
        return mapped;
      });
    }
  }, [
    reportsFilterRedux?.sites,
    isNetworkAdmin,
    classesTemplates,
    sitesTemplate,
    hasSite,
    hasClasses,
    hasRoadmap,
    selectedSite,
  ]);

  const filteredTargetGroups: Grade[] = useMemo(
    () =>
      (
        selectedSite?.targetGroups?.map((t, idx) => ({
          id: t?.id ?? '',
          name: t?.name ?? '',
          order: idx,
        })) ?? []
      ).sort((a, b) => a.name.localeCompare(b.name)),
    [selectedSite],
  );
  useEffect(() => {
    if (!singleClassDropdown) return;
    const selectedSiteId = location.state?.selectedSiteId;
    const selectedClassId = location.state?.selectedClassId;
    const selectedRoadmapId = location.state?.selectedRoadmapId;
    if (!selectedSiteId && !selectedClassId && !selectedRoadmapId) return;
    const matchedSite =
      sitesTemplate.find(site => site?.id === selectedSiteId) ??
      reportsFilterRedux?.sites ??
      (sitesTemplate.length === 1 ? sitesTemplate[0] : undefined);
    if (!matchedSite) return;
    const matchedRoadmap =
      (matchedSite?.targetGroups ?? [])
        .map((t, idx) => ({
          id: t?.id ?? '',
          name: t?.name ?? '',
          order: idx,
        }))
        .find(targetGroup => targetGroup.id === selectedRoadmapId) ?? null;
    const matchedClass =
      classesList.find(item => item.id === selectedClassId) ??
      classesTemplates
        .filter(cl => cl?.site?.id === matchedSite.id)
        .map(cl => {
          const newName = formatClassDisplayName(cl);
          return {
            ...cl,
            sectionName: newName,
            name: newName,
            grades: cl?.gradeLevels?.map(grade => grade?.name)?.join(', '),
          };
        })
        .find(item => item.id === selectedClassId) ??
      null;
    const currentSiteId = reportsFilterRedux?.sites?.id;
    const currentClassId = diagnosticReportFilters?.classes?.[0]?.id;
    const currentRoadmapId = diagnosticReportFilters?.roadmap?.id;
    const siteChanged = matchedSite?.id !== currentSiteId;
    const classChanged = (matchedClass?.id ?? null) !== (currentClassId ?? null);
    const roadmapChanged = (matchedRoadmap?.id ?? null) !== (currentRoadmapId ?? null);
    if (!siteChanged && !classChanged && !roadmapChanged) return;
    if (siteChanged) {
      dispatch(
        setReportsFilter({
          ...reportsFilterRedux,
          sites: matchedSite,
          classes: [],
        }),
      );
    }
    dispatch(
      setDiagnosticReportsFilter({
        ...diagnosticReportFilters,
        classes: matchedClass ? [matchedClass] : [],
        roadmap: matchedRoadmap,
      }),
    );
  }, [singleClassDropdown, location.state]);

  return (
    <>
      {sitesTemplate && sitesTemplate?.length > 1 && (
        <div className="field col-12 lg:col-3" style={{ marginBottom: '0' }}>
          <label htmlFor="site">Site</label>
          <Dropdown
            value={reportsFilterRedux?.sites}
            onChange={e => {
              dispatch(
                setReportsFilter({
                  ...reportsFilterRedux,
                  sites: e.value,
                  classes: [],
                  roadmaps: { id: '', name: '' },
                }),
              );
              if (singleClassDropdown) {
                dispatch(setDiagnosticReportsFilter({ ...diagnosticReportFilters, classes: [], roadmap: null }));
              }
            }}
            options={[...sitesTemplate].sort((a, b) => a?.name?.localeCompare(b?.name))}
            optionLabel="name"
            id="site"
            className="w-full"
            disabled={isCsvProcessing}
          />
        </div>
      )}
      <div className={`field col-12 lg:col-3 ${hideClassFilter ? 'tw-hidden' : ''}`} style={{ marginBottom: '0' }}>
        {singleClassDropdown ? <label htmlFor="class">Class *</label> : <label htmlFor="class">Class(es) *</label>}
        {singleClassDropdown ? (
          <div className="p-inputgroup w-full">
            <Dropdown
              onChange={(e: DropdownChangeEvent) => {
                dispatch(setDiagnosticReportsFilter({ ...diagnosticReportFilters, classes: [e.value] }));
              }}
              value={diagnosticReportFilters?.classes[0]}
              options={[...classesList]?.sort((a, b) => a?.name?.localeCompare(b?.name))}
              optionLabel="name"
              id="class"
              filter
              disabled={isCsvProcessing}
            />
          </div>
        ) : (
          <div className="p-inputgroup w-full">
            <MultiSelect
              onChange={(e: MultiSelectChangeEvent) => {
                dispatch(setReportsFilter({ ...reportsFilterRedux, classes: e.value }));
              }}
              value={reportsFilterRedux?.classes}
              options={[...classesList].sort((a, b) => a?.name?.localeCompare(b?.name))}
              optionLabel="name"
              display="chip"
              id="class"
              filter
              className="overflow-y-scroll h-3rem"
              disabled={isCsvProcessing}
            />
          </div>
        )}
      </div>
      <div className="field col-12 lg:col-3" style={{ marginBottom: '0' }}>
        <label htmlFor="roadmap">{roadmapRequired ? 'Roadmap *' : 'Roadmap'}</label>
        <Dropdown
          value={singleClassDropdown ? diagnosticReportFilters.roadmap : reportsFilterRedux.roadmaps}
          onChange={e => {
            if (singleClassDropdown) {
              dispatch(setDiagnosticReportsFilter({ ...diagnosticReportFilters, roadmap: e.value }));
            } else {
              dispatch(setReportsFilter({ ...reportsFilterRedux, roadmaps: e.value }));
            }
          }}
          options={filteredTargetGroups}
          optionLabel="name"
          id="roadmap"
          className="w-full"
          disabled={isCsvProcessing || !selectedSite}
        />
      </div>
      <ClassesRosterSelectClassDialog
        visibleSelectClassDialog={visibleSelectClassDialog}
        setVisibleSelectClassDialog={setVisibleSelectClassDialog}
        selectedClasses={reportsFilterRedux?.classes}
        setSelectedClasses={classes => {
          dispatch(setReportsFilter({ ...reportsFilterRedux, classes }));
        }}
        classesList={classesList}
        multiple
      />
    </>
  );
};

export default MonitoringCommonFilters;
